import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactElement,
} from 'react';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {CustomMenuItem} from '../../../components/common/arr-menu/ArrMenu';
import './ExpandableTreeView.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {Box} from '@mui/material';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import DocExplorerLeftTreeView from './DocExplorerLeftTreeView';

export const TEST_IDS = {
  CONTAINER: 'expandable-tree-view',
  TREE_CONTENT: 'tree-content',
  RESIZE_HANDLE: 'resize-handle',
  MORE_VERT_ICON: 'more-vert-icon',
  LOADING_SPINNER: 'loading-spinner',
} as const;

interface ExpandableTreeViewProps {
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  setSelectedItem?: (
    value: string[] | null,
    isCtrlPressed?: boolean,
    node?: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  selectedItem?: string;
  treeSelectedItems?: string[] | null;
  multipleSelection?: boolean;
  isShowCountLabel?: boolean;
  handleNoResultMessage?: string;
  allowGlobalSelect?: boolean;
  expandedNodes?: string[];
  collapsedNodes?: string[];
  onExpandedNodesChange?: (expandedNodes: string[]) => void;
  onCollapsedNodesChange?: (collapsedNodes: string[]) => void;
  setExpandedFolders?: (data: string[]) => void;
  showActions?: boolean;
  showCheckmark?: boolean;
  selectedNodeToMove?: string;
  updateTreeData?: React.Dispatch<
    React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>
  >;
  isShowCheckbox?: boolean;
  MoreMenuOptions?: CustomMenuItem[];
  onMenuItemClick?: (item: CustomMenuItem) => void;
  customContentClass?: string;
  customContentHeight?: number;
  onNodeExpand?: (
    nodeId: string
  ) => Promise<TreeViewBaseItem<ExtendedTreeItemProps>[]>;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  headerComponent?: ReactElement;
  footerComponent?: ReactElement;
}

const ExpandableTreeView: React.FC<ExpandableTreeViewProps> = ({
  initialWidth = 300,
  minWidth = 72,
  maxWidth,
  headerComponent,
  footerComponent,
  ...treeViewProps
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  useEffect(() => {
    if (!maxWidth) {
      const updateMaxWidth = () => {
        const viewportWidth = window.innerWidth;
        setMaxWidth(viewportWidth - 32);
      };

      updateMaxWidth();
      window.addEventListener('resize', updateMaxWidth);
      return () => window.removeEventListener('resize', updateMaxWidth);
    }
  }, [maxWidth]);

  const [maxWidthState, setMaxWidth] = useState(
    maxWidth || window.innerWidth - 32
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - startXRef.current;
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidthState, startWidthRef.current + deltaX)
      );

      setWidth(newWidth);
    },
    [isResizing, minWidth, maxWidthState]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box
      ref={containerRef}
      className={'expandable-tree-view'}
      style={{width: `${width / 16}rem`}}
      data-testid={TEST_IDS.CONTAINER}
    >
      <Box className="tree-content-wrapper">
        {headerComponent}
        <Box
          ref={scrollContainerRef}
          className="tree-content"
          data-testid={TEST_IDS.TREE_CONTENT}
        >
          {Array.isArray(treeViewProps.itemsList) &&
          treeViewProps.itemsList.length > 0 ? (
            <DocExplorerLeftTreeView
              {...treeViewProps}
              itemsList={treeViewProps.itemsList || []}
              scrollContainerRef={scrollContainerRef}
            />
          ) : (
            <ArrLoadingSpinner
              show={true}
              overlay={false}
              data-testid={TEST_IDS.LOADING_SPINNER}
            />
          )}
        </Box>
        {footerComponent}
      </Box>
      <Box
        className="resize-handle"
        onMouseDown={handleMouseDown}
        data-testid={TEST_IDS.RESIZE_HANDLE}
      >
        <MoreVertIcon data-testid={TEST_IDS.MORE_VERT_ICON} />
      </Box>
    </Box>
  );
};

export default ExpandableTreeView;
